import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Music, Plus, FileText, Calendar, Eye, Award, Loader2, ArrowLeft, Globe, Lock, Volume2, VolumeX, Upload, AlertCircle } from 'lucide-react';
import { worksAPI } from '../services/api';
import { toast } from 'sonner';

const WorksPage = () => {
  const navigate = useNavigate();
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWorks();
  }, []);

  const loadWorks = async () => {
    try {
      setLoading(true);
      const response = await worksAPI.list();
      setWorks(response.data);
    } catch (error) {
      console.error('Error loading works:', error);
      toast.error('Erro ao carregar obras');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublic = async (workId, currentStatus, hasAudio, e) => {
    e.stopPropagation();
    
    // Se não tem áudio e está tentando tornar público
    if (!hasAudio && !currentStatus) {
      toast.error('Para tornar a obra pública, você precisa adicionar um áudio primeiro.', {
        description: 'Clique em "Ver Detalhes" e faça upload do arquivo de áudio.',
        duration: 5000
      });
      return;
    }

    const newStatus = !currentStatus;
    
    try {
      await worksAPI.togglePublic(workId, newStatus);
      
      // Atualizar o estado local
      setWorks(works.map(work => 
        work.id === workId 
          ? { ...work, public_gallery: newStatus }
          : work
      ));
      
      toast.success(
        newStatus 
          ? '🌐 Obra agora está pública na galeria!' 
          : '🔒 Obra removida da galeria pública',
        {
          description: newStatus 
            ? 'Qualquer pessoa pode ver e ouvir sua música.' 
            : 'Apenas você pode acessar esta obra.',
          duration: 4000
        }
      );
    } catch (error) {
      console.error('Error toggling public status:', error);
      toast.error('Erro ao atualizar visibilidade da obra');
    }
  };

  const getWorkTypeLabel = (type) => {
    const types = {
      letra: 'Letra',
      musica: 'Música',
      partitura: 'Partitura',
      letra_e_musica: 'Letra e Música',
      arranjo: 'Arranjo',
      producao: 'Produção',
      video: 'Vídeo'
    };
    return types[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Rascunho' },
      registered: { color: 'bg-blue-100 text-blue-800', label: 'Registrado' },
      verified: { color: 'bg-green-100 text-green-800', label: 'Verificado' }
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando obras...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Voltar
        </button>
        
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Minhas Obras</h1>
          <Link
            to="/works/new"
            className="flex items-center gap-2 bg-gradient-primary text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            Registrar Nova Obra
          </Link>
        </div>

        {/* Works Grid */}
        {works.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center shadow-lg">
            <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Você ainda não tem obras registradas</p>
            <Link
              to="/works/new"
              className="inline-block bg-gradient-primary text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              Registrar Primeira Obra
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {works.map((work) => (
              <div
                key={work.id}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100"
              >
                {/* Status Badges */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex gap-2 flex-wrap">
                    {getStatusBadge(work.status)}
                    
                    {/* Audio Badge */}
                    {work.audio_file_id ? (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 flex items-center gap-1">
                        <Volume2 className="w-3 h-3" />
                        Com Áudio
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 flex items-center gap-1">
                        <VolumeX className="w-3 h-3" />
                        Sem Áudio
                      </span>
                    )}
                  </div>
                  
                  {work.files_count > 0 && (
                    <div className="flex items-center gap-1 text-gray-500">
                      <FileText className="w-4 h-4" />
                      <span className="text-xs">{work.files_count}</span>
                    </div>
                  )}
                </div>

                {/* Title */}
                <h3 
                  className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 cursor-pointer hover:text-blue-600 transition-colors"
                  onClick={() => navigate(`/works/${work.id}`)}
                >
                  {work.title}
                </h3>

                {/* Type and Genre */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-gray-600">
                    {getWorkTypeLabel(work.work_type)}
                  </span>
                  {work.genre && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-600">{work.genre}</span>
                    </>
                  )}
                </div>

                {/* Date */}
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {new Date(work.timestamp).toLocaleDateString('pt-BR')}
                  </span>
                </div>

                {/* Public Gallery Toggle */}
                <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {work.public_gallery ? (
                        <Globe className="w-4 h-4 text-green-600" />
                      ) : (
                        <Lock className="w-4 h-4 text-gray-500" />
                      )}
                      <span className="text-sm font-medium text-gray-700">
                        {work.public_gallery ? 'Pública na Galeria' : 'Obra Privada'}
                      </span>
                    </div>
                    
                    <button
                      onClick={(e) => handleTogglePublic(work.id, work.public_gallery, work.audio_file_id, e)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        work.public_gallery 
                          ? 'bg-green-500' 
                          : work.audio_file_id 
                            ? 'bg-gray-300 hover:bg-gray-400' 
                            : 'bg-gray-200 cursor-not-allowed'
                      }`}
                      disabled={false}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          work.public_gallery ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  {work.public_gallery && (
                    <p className="text-xs text-green-700 mt-1">
                      ✓ Visível na galeria pública
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/works/${work.id}`);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all"
                  >
                    <Eye className="w-4 h-4" />
                    Ver Detalhes
                  </button>
                  
                  {!work.audio_file_id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/works/${work.id}`);
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-all"
                      title="Adicionar áudio"
                    >
                      <Upload className="w-4 h-4" />
                      + Áudio
                    </button>
                  )}
                  
                  {work.status === 'registered' || work.status === 'verified' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/works/${work.id}`);
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-all"
                    >
                      <Award className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorksPage;
